import { useEffect, useState } from "react";

import { CAPTURE_DRAWER_FULL_BLEED_MAX_PX } from "./captureDrawerMetrics";

/**
 * The media query that decides whether the Captured drawer is PUSHING or
 * COVERING. Deliberately built from the same constant the drawer's own width
 * rule and the layout root's push rule read, so the three can never disagree
 * about which mode the app is in.
 */
export const CAPTURE_DRAWER_PUSH_MODE_QUERY = `(min-width: ${
  CAPTURE_DRAWER_FULL_BLEED_MAX_PX + 1
}px)`;

/**
 * useCaptureDrawerPushMode — is the Captured drawer currently a PUSH pane
 * (beside the page, which stays live) rather than a MODAL sheet (over it)?
 *
 * WHY IT EXISTS. The drawer used to claim `aria-modal="true"` unconditionally.
 * That is a promise to assistive tech that everything outside the dialog is
 * unavailable — and in push mode it is false: the builder is deliberately
 * still visible and still operable beside the drawer, which is the entire
 * point of QA-67's push. Left as-is, a screen-reader user gets exactly the
 * defect a pointer user got from the scrim (measured on web-stg
 * main.529b92bf.js, 2026-08-05: Save unreachable while the drawer was open) —
 * only silently, and with no scrim to blame.
 *
 * Fails SAFE in an environment without `matchMedia` (jsdom implements none):
 * it reports push mode, matching the desktop viewport every builder session
 * actually runs at, rather than throwing inside a render.
 */
export default function useCaptureDrawerPushMode() {
  const [isPushMode, setIsPushMode] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(CAPTURE_DRAWER_PUSH_MODE_QUERY);
    if (!mql) return undefined;

    setIsPushMode(!!mql.matches);
    const onChange = (e) => setIsPushMode(!!e.matches);

    // Safari <14 only has the deprecated addListener/removeListener pair.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  return isPushMode;
}
